import { postHostedDocumentsByIdUploadBySideMutation, postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import type { DocumentResponse } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { getLogger, trackAction } from '@/idv2/utils/logger';
import { DOC_UPLOAD_TIMEOUT_MS, SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import { IdDocImageUploadError } from '../../../types';
import { bytesToMegabytes } from '../../../utils/capture';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import { useSelfieMachine } from '../../context/selfie-context';
import ProcessingView from './processing-view';
const {
  logWarn,
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'selfie-processing'
});

// Only a request that got no answer, or a server error, can go better a second time: a 4xx fails the
// same way again, and a timed-out upload already made the user wait a minute.
const isWorthRetrying = (error: unknown) => error instanceof TypeError || ((error as {
  status?: number;
} | null)?.status ?? 0) >= 500;
const Processing = () => {
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, dispatch] = useSelfieMachine();
  const submitDocMutation = useSubmitDoc();
  const processDocMutation = useMutation(postHostedUserDocumentsByIdProcessMutation());
  const uploadVideoMutation = useMutation(postHostedDocumentsByIdUploadBySideMutation());
  const [mode, setMode] = useState<'loading' | 'success'>('loading');
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState<'upload' | 'analyze'>('upload');
  const slowConnectionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    image,
    authToken,
    id,
    hasBadConnectivity,
    requirement
  } = state.context;
  const isVideoRequired = requirement.config.kind === 'selfie' && requirement.config.videoCapture === 'required';
  useEffect(() => {
    trackAction('selfie-processing:started');
  }, []);
  const handleError = (err: unknown) => {
    const backendMessage = getBackendErrorMessage(err);
    dispatch({
      type: 'processingErrored',
      payload: {
        errors: [backendMessage ? {
          errorType: 'custom_error',
          errorInfo: backendMessage
        } : {
          errorType: IdDocImageUploadError.unknownUploadError
        }]
      }
    });
  };
  const handleProcessDocSuccess = (data: DocumentResponse) => {
    const {
      errorDetails,
      nextSideToCollect,
      isRetryLimitExceeded
    } = data;
    if (isRetryLimitExceeded) {
      logWarn(`Selfie upload retry limit exceeded. doc id: ${id}`);
      setRetryLimitExceeded(true);
      return;
    }
    // A selfie is single-sided: any next side means the capture was rejected.
    if (nextSideToCollect) {
      logWarn('Selfie rejected');
      dispatch({
        type: 'processingErrored',
        payload: {
          errors: errorDetails.map(err =>
          // The user can't fix a missing clip, so it gets the same retake as a failed upload.
          err.code === 'selfie_video_missing' ? {
            errorType: IdDocImageUploadError.unknownUploadError
          } : {
            errorType: err.code ?? 'custom_error',
            errorInfo: err.message
          })
        }
      });
      return;
    }
    setMode('success');
    trackAction('selfie-processing:completed');
  };

  // The clip must land before /process, which completes the document. Resolves to whether the selfie
  // can go on: only a required clip that didn't upload stops it.
  const uploadVideo = async (documentId: string) => {
    const videoFile = image?.videoFile;
    if (!videoFile || hasBadConnectivity && !isVideoRequired) return true;
    const clip = {
      sizeMb: bytesToMegabytes(videoFile.size),
      type: videoFile.type
    };
    const tryUpload = async () => {
      const startedAt = Date.now();
      try {
        await uploadVideoMutation.mutateAsync({
          body: {
            fileData: videoFile
          },
          path: {
            id: documentId,
            side: 'selfie_video'
          },
          headers: {
            'X-Fp-Authorization': authToken
          },
          signal: AbortSignal.timeout(DOC_UPLOAD_TIMEOUT_MS)
        });
        logInfo('Selfie video uploaded', {
          ...clip,
          durationMs: Date.now() - startedAt
        });
        return 'uploaded';
      } catch (error) {
        logWarn('Selfie video upload failed', error, {
          ...clip,
          durationMs: Date.now() - startedAt
        });
        return isWorthRetrying(error) ? 'retry' : 'failed';
      }
    };
    const result = await tryUpload();
    if (!isVideoRequired) return true;
    return (result === 'retry' ? await tryUpload() : result) === 'uploaded';
  };
  const handleSubmitDocSuccess = async () => {
    if (!id) return;
    if (!(await uploadVideo(id))) {
      // Shown like a failed image upload, so the user retakes without spending an attempt.
      dispatch({
        type: 'processingErrored',
        payload: {
          errors: [{
            errorType: IdDocImageUploadError.unknownUploadError
          }]
        }
      });
      return;
    }
    setStep('analyze');
    try {
      const response = await processDocMutation.mutateAsync({
        path: {
          id
        },
        headers: {
          'X-Fp-Authorization': authToken
        }
      });
      handleProcessDocSuccess(response);
    } catch (error) {
      handleError(error);
      logWarn(`Error while processing selfie ${id}: ${getErrorMessage(error)}`, error);
    }
  };
  const handleSubmitDocError = (err: unknown) => {
    if (getErrorCode(err) === 'E109') {
      setMode('success');
      return;
    }
    logWarn(`Selfie submit failed. upload session id: ${id}. Error: ${getErrorMessage(err)}`, err);
    handleError(err);
  };
  useEffect(() => {
    if (!image || !authToken || !id) {
      setIsMissingRequirements(true);
      logWarn('Selfie could not be processed due to missing requirements');
      return;
    }
    if (!slowConnectionTimer.current) {
      slowConnectionTimer.current = setTimeout(() => {
        if (submitDocMutation.isSuccess) return;
        setShowSlowConnectionMessage(true);
      }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
    }
    const {
      imageFile,
      extraCompressed,
      captureKind
    } = image;
    logInfo(`Uploading selfie with size ${bytesToMegabytes(imageFile.size)} MB and type ${imageFile.type}`);
    submitDocMutation.mutate({
      body: {
        fileData: imageFile
      },
      path: {
        id,
        side: 'selfie'
      },
      headers: {
        'X-Fp-Authorization': authToken,
        'X-Fp-Is-Extra-Compressed': String(extraCompressed),
        'X-Fp-Is-Manual': String(captureKind === 'manual'),
        'X-Fp-Is-App-Clip': 'false',
        'X-Fp-Is-Instant-App': 'false',
        'X-Fp-Is-Upload': String(captureKind === 'upload'),
        'X-Fp-Is-Forced-Upload': 'false'
      }
    }, {
      onSuccess: handleSubmitDocSuccess,
      onError: handleSubmitDocError,
      onSettled: () => {
        if (slowConnectionTimer.current) {
          clearTimeout(slowConnectionTimer.current);
          setShowSlowConnectionMessage(false);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <ProcessingView mode={mode} step={step} showSlowConnectionMessage={showSlowConnectionMessage} retryLimitExceeded={retryLimitExceeded} isMissingRequirements={isMissingRequirements} onSuccess={() => dispatch({
    type: 'processingSucceeded'
  })} onRetryLimitExceeded={() => dispatch({
    type: 'retryLimitExceeded'
  })} />;
};
export default Processing;